import update from 'immutability-helper';
import get from 'lodash/get';
import { ACTIVE_METRICS } from '@utils/metrics';
import {
  MARIO,
  CURRENT_PRODUCT_MODE,
  isAppModule,
  RT_ON_TRENDS_REFRESH_INTERVAL,
} from '@utils/constants';
import * as drilldownTypes from '@store/summaryContainer/drilldown/types';
import * as comparisonTypes from '@store/comparison/types';
import featureNames from '@utils/featureToggle/constants';
import { isGrandfatherFilter, getPresetFid } from '@utils';
import getGranularity, { getAppDefaultGranularity } from '@utils/granularity';
import * as compareDrawerTypes from '@store/compareDrawer/types';
import { DEFAULT_FILTER_COLOR } from '@utils/colorPalette';
import { getAllowedGranularity } from '@components/common/GranularitySelect/utils';
import * as types from './types';

export const initialFilter = {
  name: 'All Traffic',
  rule: [],
};

const allMetricKeys = Object.keys(ACTIVE_METRICS).reduce((total, name) => {
  total.push(...Object.keys(ACTIVE_METRICS[name as keyof typeof ACTIVE_METRICS]));
  return total;
}, [] as string[]);

export const initialState = {
  appliedFilters: [initialFilter],
  dimensionTypeInFilter: [],
  metricsToNA: [], // contains all supported metrics that are not applicable for current applied filter
  isGrandfatherFilter: false,
  filterColor: DEFAULT_FILTER_COLOR,
  compareMode: 0,
  dashboard: {
    list: [
      {
        id: 'trends',
        name: 'Trends',
        context: '',
      },
      // {
      //   id: 'all-metrics',
      //   name: 'All Metrics',
      // },
    ],
    activeId: 'trends',
  },
  interval: { from: '', to: '', shortcut: '', granularity: '' },
  isLiveModeSelected: false,
  resetDiagnostics: false,

  /* -- initialze - InitData START */
  dashboardTypes: {},
  profiles: [],
  features: {
    // Following features have pre-determined defaults. Features API response determines
    // the customer specific behavior of the feature
    [featureNames.ERROR_DIAGNOSTICS]: false,
    [featureNames.DAY_OVER_DAY]: false,
    [featureNames.SAVED_DASHBOARDS]: false,
    [featureNames.SHARE]: false,
    [featureNames.COMPARE]: true,
    [featureNames.COMBINATORIAL_FILTER]: true,
    [featureNames.TRENDS_DIAGNOSTICS]: true,
    [featureNames.MINUTE_GRANULARITY]: true,

    [featureNames.CREATE_FILTER]: true,
    [featureNames.SECONDARY_TIME_SERIES]: true,
    [featureNames.TRENDS_DIAG_COMPARE_SERIES]: true,
    [featureNames.EXPORT_DATA]: true,
    [featureNames.NOC]: false,
    [featureNames.NOC_AD_HOC]: false,
    [featureNames.NOC_SHARE]: false,
    [featureNames.NOC_SAVE]: false,
    [featureNames.REALTIME_MAX_FILTERS]: 20,
    [featureNames.REALTIME_ROWS_PER_DIMENSION]: 200,
    [featureNames.REALTIME_AI_THRESHOLD]: true,

    [featureNames.ML]: false,

    [featureNames.AI_ROADMAP]: false,

    [featureNames.VIEWER_DRILLDOWN]: false,
    [featureNames.VIEWER_DRILLDOWN_DISTRIBUTION]: false,
    [featureNames.VIEWER_DRILLDOWN_REST_METRICS]: false,
    [featureNames.DISTRIBUTION_METRICS]: false,
    [featureNames.DISTRIBUTION_METRICS_DRILLDOWN]: true,

    // Limits for the following features are set to a high value by default.
    // These limits, in practice, will never be reached. Features API response
    // replaces high limits with actual limits
    [featureNames.ENUMERATED_VALUES_PER_DIMENSION]: 200,
    [featureNames.ERROR_CODES_LIMIT]: 200,
    [featureNames.DATA_ACCESS_DURATION]: 24, // months
    [featureNames.ALL_METRICS]: allMetricKeys,
    [featureNames.METRICS]: Object.keys(ACTIVE_METRICS[CURRENT_PRODUCT_MODE]),
    [featureNames.DRILLDOWN_LIMIT]: 3,
    [featureNames.CUSTOM_TAGS]: null,
    [featureNames.EXPORT_DATA]: true,
    [featureNames.EXPORT_WIDGET_DATA]: true,
    [featureNames.VIEWER_DRILLDOWN_TIMELINE]: false,
    [featureNames.CUSTOMER_ACCOUNT_TYPE]: 0,
    [featureNames.ALERT_DIAGNOSTICS]: false,

    [featureNames.NON_BLOCKING_LOADER]: false,
    [featureNames.TOP_RISING_CONTENT]: false,

    [featureNames.NEW_TRENDS_LAYOUT]: false,
    [featureNames.NOC_BUSINESS_ERROR_METRICS]: false,

    [featureNames.EXPERIENCE_DASHBOARD]: false,
    [featureNames.EXPERIENCE_DASHBOARD_BENCHMARK]: false,
    [featureNames.EXPERIENCE_DASHBOARD_ADVANCED_BENCHMARK]: false,
    [featureNames.AUTO_RETRY]: false,
    [featureNames.OVB_SHARE]: false,
    [featureNames.OVB_SAVE]: false,
    [featureNames.UNIFIED_AD_CONTENT_OVERVIEW]: false,
    [featureNames.SI_BIGPANDA]: false,
    [featureNames.SI_DATADOG]: false,
    [featureNames.EI_PAGERDUTY]: false,
    [featureNames.METRIC_SELECTOR_POPUP]: false,
    [featureNames.APP_AI_ALERTS]: false,
    [featureNames.AI_ALERTS_1_MIN_PERSISTENCE]: false,
    [featureNames.AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT]: 0,

    [featureNames.SESSION_TIMELINE_30DAYS_SAVE]: false,
    [featureNames.PRECISION_POLICY_SCHEDULING]: false,
    [featureNames.CAN_EDIT_PRECISION_POLICY_FILTER]: false,
    [featureNames.WIDGET_PANEL]: false,
    [featureNames.RT_TRENDS_REFRESH_RATE]: RT_ON_TRENDS_REFRESH_INTERVAL,
    [featureNames.API_KEY_LIMIT]: 50,
    // viewer module
    [featureNames.ENABLE_2019_CHANGES]: false,
    [featureNames.SHOW_NINETY_DAYS_HISTORY]: false,
    [featureNames.VIEWER_MODULE_PULSE5]: false,
    [featureNames.AUTO_INSIGHTS]: false,
    [featureNames.AI_ALERTS_PCP]: false,
    [featureNames.AI_ALERT_FUSION_VSI]: false,
    [featureNames.MUTE_AI_ALERT_NOTIFICATIONS]: true,
    [featureNames.AI_ALERTS_CIRR]: false,
    [featureNames.VIEWERS_OVERVIEW]: false,
    [featureNames.VIEWERS_BROWSER_TAB]: false,
    [featureNames.KPI_DROPDOWN]: false,
    [featureNames.TIME_GRANULARITY]: false,
    [featureNames.ANNOTATIONS]: false,
    [featureNames.HORIZONTAL_LAYOUT]: false,
    [featureNames.PUBLIC_SAVED_DASHBOARDS]: false,
    [featureNames.REALTIME_PSD]: false,
    [featureNames.RT_ON_TRENDS_LAST_15]: false,
    [featureNames.VM_LIVE_TLB]: false,
    [featureNames.PROMPT]: false,
    // DPI
    [featureNames.APP_EXPERIENCE]: false,
    [featureNames.APP_USER_TIMELINE]: false,
    [featureNames.APP_EXPERIENCE_BACKEND]: 'mario',
    [featureNames.APP_RT_ON_TRENDS]: true,
    [featureNames.APP_INSIGHTS_METRIC_FILTERING]: false,
    [featureNames.APP_INSIGHTS_DISTRIBUTION]: false,
    [featureNames.APP_EXPERIENCE_SAVE_AND_SHARE]: false,
    [featureNames.APP_SPI]: false,
    [featureNames.AUDIENCE_ENGAGEMENT_METRICS]: false,
    [featureNames.AI_ALERTS_CUSTOM_METRICS_UPPER_LIMIT]: 0,
    [featureNames.AUTO_Y_AXIS]: false,
    [featureNames.PERSONAL_PRESETS]: false,

    // frontend flag to disable new tab feature
    [featureNames.NEW_BROWSER_TAB_WORKFLOW]: false,
    [featureNames.ECO_AI_ALERT_2]: 0,
    [featureNames.SELF_SERVE]: false,
    [featureNames.SELF_SERVE_INTEGRATION_STATUS]: false,
    [featureNames.DPI_NEXA_PI_SUMMARY]: false,

    [featureNames.MGW_SAMPLING_CUTOVER_DATE]: '',
    [featureNames.GW_THROTTLING_START_DATE]: '',
  },
  /* -- initialze - InitData END */
  isSharingPage: false,
  userDefinedMetrics: {},
  appBackendType: MARIO,
  aiAlertsDimensionMapping: {},
};

const reducerMap = Object.create(null);

reducerMap[drilldownTypes.SELECT_COMPARE_MODE] = (state, action) =>
  update(state, {
    compareMode: {
      $set: action.mode,
    },
  });

reducerMap[types.RESET] = (state) =>
  update(state, {
    $merge: {
      appliedFilters: initialState.appliedFilters,
      resetDiagnostics: false,
    },
  });

reducerMap[types.SET_COMPARE_MODE] = reducerMap[drilldownTypes.SELECT_COMPARE_MODE];

reducerMap[comparisonTypes.MODIFY_COMPARISON] = (state, action) => {
  const { interval, appliedFilters } = action.config;
  const { resetDiagnostics } = action;
  const granularity =
    interval.granularity || getGranularity({ from: interval.from, to: interval.to });

  return update(state, {
    compareMode: { $set: action.mode },
    interval: { $set: { ...interval, granularity } },
    resetDiagnostics: {
      $set: resetDiagnostics,
    },
    $merge: {
      appliedFilters,
    },
  });
};

reducerMap[compareDrawerTypes.EXIT_COMPARE_MODE] = (state) =>
  update(state, {
    compareMode: {
      $set: 0,
    },
  });

reducerMap[types.SET_REAL_TIME_MODE] = (state, action) =>
  update(state, {
    isLiveModeSelected: {
      $set: action.flag,
    },
  });

reducerMap[types.INTERVAL_SELECTION] = (state, action) => {
  let granularity = getGranularity({
    from: action.from,
    to: action.to,
    minuteGranularity: true,
    overview: false,
    shortcut: action.shortcut,
  });
  if (isAppModule) {
    granularity = getAppDefaultGranularity({
      from: action.from,
      to: action.to,
      minuteGranularity: true,
      overview: false,
      shortcut: action.shortcut,
      dashboardType: action.dashboardType,
    });
  }
  return update(state, {
    interval: {
      $set: {
        from: action.from,
        to: action.to,
        shortcut: action.shortcut,
        granularity: granularity,
      },
    },
  });
};
reducerMap[types.UPDATE_GRANULARITY] = (state, action) =>
  update(state, {
    interval: {
      $merge: {
        granularity: action.granularity,
      },
    },
  });

reducerMap[types.CHANGE_IS_SHARING_PAGE] = (state, action) =>
  update(state, {
    isSharingPage: {
      $set: action.isSharingPage,
    },
  });

reducerMap[types.UPDATE_PROFILES] = (state, action) => {
  const { profiles } = action;
  const tzOffset = get(
    profiles.filter((p) => p.is_selected),
    '0.timezone',
  );
  // const features = get(profiles.filter(p => p.is_selected), '0.features');
  return update(state, {
    $merge: {
      profiles,
      tzOffset,
      // features,
    },
  });
};

reducerMap[types.UPDATE_FEATURES] = (state, action) => {
  const { features } = action;
  return update(state, {
    features: {
      $merge: {
        ...features,
      },
    },
  });
};

reducerMap[types.UPDATE_APP_ALL_METRICS] = (state, action) => {
  const { appAllMetrics } = action;
  return update(state, {
    features: {
      $merge: { app_metrics: appAllMetrics },
    },
  });
};

reducerMap[types.SET_DIMENSION_TYPE_IN_FILTER] = (state, action) => {
  const { data } = action;
  return update(state, {
    dimensionTypeInFilter: {
      $set: data,
    },
  });
};

reducerMap[types.UPDATE_USER_DEFINED_METRICS] = (state, action) =>
  update(state, {
    userDefinedMetrics: {
      $set: action.userDefinedMetrics,
    },
  });

reducerMap[types.APPLY_FILTER] = (state, action) => {
  const { filter } = action;
  const { name, rule = [], fid, is_advanced: filterIsAdvanced, isAdvanced } = filter;
  const { features } = state;
  const grandfatherFilter = isGrandfatherFilter(filter, features[featureNames.CUSTOM_TAGS]);

  const extraParams = {};
  // refresh key for reloading trends
  if ('_rKey' in filter) {
    extraParams['_rKey'] = filter['_rKey'];
  }

  // use passed in preset or check for a preset filter match
  const presetFid = filter.presetFid || getPresetFid(rule);

  return update(state, {
    $merge: {
      appliedFilters: [
        {
          name,
          rule,
          fid,
          isAdvanced: filterIsAdvanced || isAdvanced,
          presetFid,
          ...extraParams,
        },
      ],
      isGrandfatherFilter: grandfatherFilter,
    },
  });
};

reducerMap[types.CHANGE_ACTIVE_DASHBOARD_ID] = (state, action) =>
  update(state, {
    dashboard: {
      activeId: {
        $set: action.dashboardId,
      },
    },
  });

reducerMap[types.ADD_NEW_DASHBOARD] = (state, action) => {
  const { dashboard } = state;
  const { newDashboard } = action;
  const last = dashboard[dashboard.length - 1];

  if (last.isNew === true) {
    return state;
  }
  newDashboard.isNew = true;
  return update(state, {
    dashboard: {
      list: {
        $push: [newDashboard],
      },
    },
  });
};

reducerMap[types.TOGGLE_MODAL] = (state, action) => {
  const { modalType } = action;
  const modalConfig = {
    modalType,
  };
  return update(state, {
    modal: {
      $set: modalConfig,
    },
  });
};

reducerMap[types.SET_SHARING_STATE] = (state, action) => {
  // backward compatibility for old shared dashboards
  const { interval } = action.sharingState;

  // validate granularity
  if (interval.granularity) {
    const allowedValues = getAllowedGranularity(interval);
    if (!allowedValues.includes(interval.granularity)) {
      interval.granularity = null;
    }
  }
  interval.granularity =
    interval.granularity || getGranularity({ from: interval.from, to: interval.to });

  return update(state, {
    $merge: {
      ...action.sharingState,
      interval,
    },
  });
};

reducerMap[types.RESET_DIAGNOSTICS] = (state) =>
  update(state, {
    resetDiagnostics: {
      $set: false,
    },
  });

reducerMap[types.TOGGLE_ANNOTATIONS] = (state, action) =>
  update(state, {
    annotations: {
      $set: action.value,
    },
  });

reducerMap[types.AI_ALERTS_DIMENSION_MAPPING] = (state, action) =>
  update(state, {
    aiAlertsDimensionMapping: {
      $set: action.mapData,
    },
  });

/* eslint max-len:0 */
const common = (state = initialState, action) =>
  reducerMap[action.type] ? reducerMap[action.type](state, action) : state;

export default common;
